/**
 * Sage and Co. — mobile sticky Add to Cart
 *
 * Reveals the sticky bar (rendered hidden by product-sticky-atc.liquid) once the
 * real Add to Cart button has scrolled out of view, and forwards its tap to that
 * button so all variant, quantity and ajax-cart behaviour is reused rather than
 * duplicated.
 *
 * Progressive enhancement, deliberately: if this file never runs, the bar stays
 * hidden (it ships with aria-hidden and no .is-visible class, and the stylesheet
 * keeps it translated off-screen until .is-visible is added) and the product page
 * behaves exactly as before.
 */
(function () {
  'use strict';

  function init() {
    var bar = document.querySelector('[data-sticky-atc]');
    if (!bar) return;

    // The real, in-page primary button carries the ajax path.
    var realButton = document.querySelector('.product__section--buttons .AddtoCart');
    var stickyButton = bar.querySelector('[data-sticky-atc-button]');
    if (!realButton || !stickyButton) return;

    // Shopify's signed-in preview toolbar is rendered in the browser's top
    // layer, so no z-index in the theme can paint above it. Its mobile wrapper
    // occupies the bottom of the viewport and otherwise covers this bar. Keep
    // the customer storefront at bottom: 0, but offset the bar by the actual
    // preview-toolbar height when Shopify injects it (including when injection
    // happens after this deferred script has initialised).
    var previewBar = null;
    var previewResizeObserver = null;

    function measurePreviewBar() {
      var height = previewBar
        ? Math.max(0, Math.round(previewBar.getBoundingClientRect().height))
        : 0;
      var offset = height ? height + 'px' : '';

      if (offset) {
        bar.style.setProperty('--sticky-atc-preview-offset', offset);
      } else {
        bar.style.removeProperty('--sticky-atc-preview-offset');
      }
    }

    function syncPreviewBar() {
      var nextPreviewBar = document.getElementById('PBarNextFrameWrapper');
      if (nextPreviewBar === previewBar) {
        measurePreviewBar();
        return;
      }

      if (previewResizeObserver && previewBar) {
        previewResizeObserver.unobserve(previewBar);
      }

      previewBar = nextPreviewBar;

      if (previewResizeObserver && previewBar) {
        previewResizeObserver.observe(previewBar);
      }

      measurePreviewBar();
    }

    if ('ResizeObserver' in window) {
      previewResizeObserver = new ResizeObserver(measurePreviewBar);
    }

    syncPreviewBar();

    if ('MutationObserver' in window) {
      var previewMutationObserver = new MutationObserver(syncPreviewBar);
      previewMutationObserver.observe(document.body, { childList: true });
    }

    window.addEventListener('resize', measurePreviewBar);

    // Forward the tap to the real button; the theme's delegated js-ajax-submit
    // listener handles the add from there. Guard on disabled so a sold-out bar
    // never fires an add.
    stickyButton.addEventListener('click', function () {
      if (stickyButton.hasAttribute('disabled')) return;
      realButton.click();
    });

    // Keep the bar's label, availability and price in step with the real
    // controls, so choosing a variant is reflected here too. The theme
    // re-renders #product-price and updates the real button on variant change.
    var priceTarget = bar.querySelector('[data-sticky-atc-price]');
    var priceSource = document.getElementById('product-price');

    // Only the price the shopper can see. #product-price also carries
    // visually-hidden screen-reader spans ("Regular price", "Sale price",
    // "Sold out", the unit-price block), so reading the whole container's
    // textContent dumps all of that into the bar. The container's
    // price--on-sale class decides which price-item is the visible one.
    function readVisiblePrice() {
      if (!priceSource) return '';
      var item = priceSource.classList.contains('price--on-sale')
        ? priceSource.querySelector('.price__sale [data-sale-price]')
        : priceSource.querySelector('.price__regular [data-regular-price]');
      return item ? (item.textContent || '').trim() : '';
    }

    function sync() {
      var label = (realButton.textContent || '').trim();
      if (label) stickyButton.textContent = label;

      if (realButton.hasAttribute('disabled')) {
        stickyButton.setAttribute('disabled', 'disabled');
      } else {
        stickyButton.removeAttribute('disabled');
      }

      if (priceTarget) {
        var price = readVisiblePrice();
        // On a miss keep whatever is already shown — the Liquid-rendered
        // price — rather than blanking or flooding the bar.
        if (price) priceTarget.textContent = price;
      }
    }

    sync();

    if ('MutationObserver' in window) {
      var mo = new MutationObserver(sync);
      mo.observe(realButton, {
        attributes: true,
        childList: true,
        characterData: true,
        subtree: true
      });
      if (priceSource) {
        mo.observe(priceSource, {
          childList: true,
          characterData: true,
          subtree: true
        });
      }
    }

    function setVisible(visible) {
      bar.classList.toggle('is-visible', visible);
      bar.setAttribute('aria-hidden', visible ? 'false' : 'true');
    }

    // Show the bar whenever the real button is not on screen — below the fold on
    // load, and again once the shopper scrolls past it. Without the observer the
    // bar simply stays hidden rather than being stranded on screen.
    if (!('IntersectionObserver' in window)) return;

    var observer = new IntersectionObserver(
      function (entries) {
        for (var i = 0; i < entries.length; i++) {
          setVisible(!entries[i].isIntersecting);
        }
      },
      { rootMargin: '0px', threshold: 0 }
    );

    observer.observe(realButton);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
